// IndexedDB (idb): the device's copy of everything a study session needs, plus the outboxes.
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CardRow, Inbox, LessonProgressRow, MeResponse, NewFlag, NotebookDone, ReviewRow, ReviewVoid, SharedState } from '@valyrio/engine/api';
import type { DictionaryBundle } from '@valyrio/engine/types';
import type { SerializedFormIndex } from '@valyrio/engine/analyser';
import type { FormIndexSnapshot } from '@valyrio/engine/formIndexCompact';
import type { CourseOrderFile, CoursePlanFile } from './course';
import type { DictOverlay } from './dictionary';

/** A card as stored on the device: the API row plus a dirty flag for the next push. */
export interface CardRecord extends CardRow {
  key: string; // `${type}|${itemId}`
  dirty: 0 | 1;
}

/** Any non-study write (flags, settings, pace, …) waiting to reach the API, in order. */
export interface OutboxWrite {
  seq?: number; // autoIncrement key: preserves order
  id: string; // client uuid (idempotency for the API where supported)
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string; // e.g. "/api/flags"
  body?: unknown;
  kind: string; // "flag", "me", "pace", … (for display and coalescing)
  createdAt: string;
  attempts: number;
  lastError?: string;
  /** Set when the server refused it for good: HTTP status, its reason code and message. */
  status?: number;
  reason?: string;
  message?: string;
  rejectedAt?: string;
  /** Local only, never sent: what to put back if the server refuses the write (state/actions.ts onWriteRejected). */
  undo?: unknown;
  /**
   * Yksi puhelin (together/onephone/): the write is the partner's, made on this phone. `as` is whose it is
   * (the overlays show it as theirs); it is sent as X-Acting-As, so the server records it as theirs.
   * (`pass`: the old partner pass of a write queued before 2026-10-13; never sent any more.)
   */
  as?: string;
  pass?: string;
}

/** A flag raised on this device, kept so the UI can show "flagged" before/after sync. */
export interface LocalFlag {
  key: string; // `${table}:${id}`
  flag: NewFlag;
  createdAt: string;
  serverId?: string;
}

export interface KvMap {
  me: MeResponse;
  shared: SharedState;
  sharedAt: string;
  lastSync: string; // server `now` of the last successful /api/sync
  lessonProgress: LessonProgressRow[];
  dictOverlay: DictOverlay;
  bundleSource: 'bundle' | 'fixture';
  courseOrder: CourseOrderFile;
  coursePlan: CoursePlanFile;
  deadWrites: OutboxWrite[]; // rejected by the API (4xx); kept for the review queue to show
  demoSeeded: boolean;
  todayLocal: { date: string; reviews: number; newWords: number; notebook?: number }; // this device's own count for today
  /** Notebook tasks marked done on this device (recent days; the server has them too). */
  notebook: NotebookDone[];
  /** Today's notebook tasks as generated, kept for the day (notebook/plan.ts NotebookPlan). */
  notebookPlan: unknown;
  /** SyncPull.dictVersion of the last complete dictionary pull (pull again only when it differs). */
  dictVersion: string;
  /** What waits for this person (SyncPull.inbox). */
  inbox: Inbox;
  /** When this person last opened the letters (sent with each sync for the inbox counts). */
  lettersSeenAt: string;
  /**
   * The engine's form index for the bundle (lib/lexicon.ts); `stamp` = bundle version + engine hash.
   * Stored as a compact snapshot (typed arrays: fast to read back); an older device may still hold
   * the serialized form until the lexicon rewrites it.
   */
  formIndex: SerializedFormIndex | FormIndexSnapshot;
  /** A finished shared session whose result has not reached the server yet (routes/Shared.tsx). */
  sharedPending: { sessionId: string; answers: unknown[]; score: number };
  /**
   * Reviews undone ("Kumoa") after they may already have reached the server: the sync sends them
   * so the server removes the review_log row and takes it off study_day (see ReviewVoid).
   */
  reviewVoids: ReviewVoid[];
  /** Last good response of an API list (letters, flags, proposals …), for offline display (lib/remote.ts). */
  [remote: `remote:${string}`]: unknown;
}

/**
 * An undone review the server may already have: sent as `SyncPush.voidReviews` with every push
 * (lib/sync.ts) and dropped from kv once a push carrying it succeeded. The server deletes the
 * review_log row `id` of this user and, only when a row was deleted, takes it off study_day
 * (engine/src/api.ts ReviewVoid) — idempotent, like review ingestion.
 */
export type { ReviewVoid };
export type KvKey = keyof KvMap;

export interface ValyrioSchema extends DBSchema {
  kv: { key: string; value: unknown };
  bundle: { key: string; value: DictionaryBundle };
  cards: { key: string; value: CardRecord; indexes: { dirty: number; due: string } };
  reviewOutbox: { key: string; value: ReviewRow };
  writeOutbox: { key: number; value: OutboxWrite };
  lessonOutbox: { key: string; value: LessonProgressRow };
  localFlags: { key: string; value: LocalFlag };
}

export type ValyrioDB = IDBPDatabase<ValyrioSchema>;

export const DB_NAME = 'valyrio';
export const DB_VERSION = 1;

/**
 * Connection events the app reacts to (state/actions.ts):
 * - `blocked`: this page wants a newer version while another tab holds the old one open;
 * - `versionchange`: another tab wants a newer version — this connection is closed at once so
 *   the other tab can upgrade (the app then reloads to pick up the new code);
 * - `closed`: the browser closed the connection (WebKit "connection lost", storage cleared);
 *   the next getDB() reopens it.
 */
export type DbEvent = 'blocked' | 'versionchange' | 'closed';
const dbListeners = new Set<(e: DbEvent) => void>();
export function onDbEvent(l: (e: DbEvent) => void): () => void {
  dbListeners.add(l);
  return () => dbListeners.delete(l);
}
const emitDb = (e: DbEvent) => {
  for (const l of dbListeners) {
    try {
      l(e);
    } catch {
      /* a listener never breaks the database */
    }
  }
};

export function openValyrioDB(name = DB_NAME): Promise<ValyrioDB> {
  let conn: ValyrioDB | null = null;
  const p = openDB<ValyrioSchema>(name, DB_VERSION, {
    blocked() {
      emitDb('blocked');
    },
    blocking() {
      conn?.close();
      if (name === DB_NAME) shared = null;
      emitDb('versionchange');
    },
    terminated() {
      if (name === DB_NAME) shared = null;
      emitDb('closed');
    },
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        db.createObjectStore('kv');
        db.createObjectStore('bundle');
        const cards = db.createObjectStore('cards', { keyPath: 'key' });
        cards.createIndex('dirty', 'dirty');
        cards.createIndex('due', 'due');
        db.createObjectStore('reviewOutbox', { keyPath: 'id' });
        db.createObjectStore('writeOutbox', { keyPath: 'seq', autoIncrement: true });
        db.createObjectStore('lessonOutbox', { keyPath: 'lessonId' });
        db.createObjectStore('localFlags', { keyPath: 'key' });
      }
      // Future versions: add `if (oldVersion < 2) { … }` blocks; never drop user data.
    },
  });
  return p.then((db) => (conn = db));
}

let shared: Promise<ValyrioDB> | null = null;
/**
 * The app's database (one connection per page). A failed open is not cached: the next call tries
 * again (WebKit loses its IndexedDB connection after backgrounding now and then).
 */
export function getDB(): Promise<ValyrioDB> {
  if (!shared) {
    let p: Promise<ValyrioDB>;
    try {
      p = openValyrioDB();
    } catch (err) {
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
    shared = p;
    p.catch(() => {
      if (shared === p) shared = null;
    });
  }
  return shared;
}

let persistAsked = false;
/**
 * Ask once per page for persistent storage, so the browser does not evict the device's copy
 * (and the unsent grades in the outbox) under disk pressure. Never prompts on iOS/Chrome; the
 * browser decides from engagement and installation.
 */
export function requestPersistentStorage(): void {
  if (persistAsked) return;
  persistAsked = true;
  try {
    const s = typeof navigator !== 'undefined' ? navigator.storage : undefined;
    if (!s?.persist) return;
    void (s.persisted ? s.persisted() : Promise.resolve(false)).then((already) => (already ? undefined : s.persist())).catch(() => undefined);
  } catch {
    /* not supported */
  }
}

export async function kvGet<K extends KvKey>(db: ValyrioDB, key: K): Promise<KvMap[K] | undefined> {
  return (await db.get('kv', key)) as KvMap[K] | undefined;
}
export async function kvSet<K extends KvKey>(db: ValyrioDB, key: K, value: KvMap[K]): Promise<void> {
  await db.put('kv', value, key);
}

export function toCardRow(r: CardRecord): CardRow {
  const { key: _k, dirty: _d, ...row } = r;
  return row;
}
